import { Type } from 'class-transformer';
import dayjs from 'dayjs';
import { Address } from './address';
import { Contact } from './contact';
import { Dispute } from './dispute';
import { Tag } from './tag';
import { User } from './user';
import { ApPortal, ApPortalProvider } from './ap_portal';
import { type Invoice, InvoiceStatus } from './invoice';
import { Model } from './model';
import type { PartnerBase } from './partner-base';
import { UnifiedModelMixin } from './unified_model';
import { TransformStringToDayJS } from './utils';
import { DunningState, DunningStateAction } from './dunning_state';
import type { OutreachChannel } from './types';
import { defaultTimezone } from '@ui/components/time/tz';

export class Partner extends UnifiedModelMixin implements PartnerBase {
  id: string;

  account_number: string | null;

  business_unit_id: string | null;

  @Type(() => User)
  assignee: User | null;

  @Type(() => ApPortal)
  ap_portals: ApPortal[] | null;

  balance: number;

  @Type(() => Address)
  billing_address: Address;

  call_outreach_pause_reason: string | null;

  @Type(() => Contact)
  contacts: Contact[];

  currency: string | null;

  custom_fields: Record<string, { label: string; value: string }> | null;

  description: string | null;

  @Type(() => Dispute)
  disputes: Dispute[];

  email: string | null;

  email_outreach_pause_reason: string | null;

  family_name: string | null;

  given_name: string | null;

  invoice_balance: number;

  invoice_prefix: string | null;

  invoices: Invoice[];

  is_active: boolean;

  is_company: boolean;

  is_customer: boolean;

  is_primary_email: boolean;

  is_primary_phone: boolean;

  is_email_disabled: boolean;

  is_phone_disabled: boolean;

  is_sms_disabled: boolean;

  is_email_outreach_manually_paused: boolean | null;

  is_call_outreach_manually_paused: boolean | null;

  is_xls_attachments_enabled: boolean;

  is_supplier: boolean;

  last_outreach_to: string | null;

  @TransformStringToDayJS()
  last_outreach_at: dayjs.Dayjs | null;

  last_outreach_channel: OutreachChannel | null;

  legal_name: string | null;

  name: string;

  num_open_invoices: number;

  num_overdue_invoices: number;

  @TransformStringToDayJS()
  oldest_open_invoice_due_date: dayjs.Dayjs | null;

  on_behalf_of: string | null;

  on_behalf_of_phone: string | null;

  parent_id: string | null;

  @Type(() => Partner)
  parent: Partner | null;

  phone: string | null;

  remote_id: string;

  remote_payment_portal_url: string | null;

  retell_language_code: string | null;

  @Type(() => Address)
  shipping_address: Address;

  dunning_state_id: string | null;

  @Type(() => DunningState)
  dunning_state: DunningState | null;

  // "YYYY-MM-DD" when on a temporary pause (auto-resumes on this date), null when indefinite / not paused
  dunning_state_pause_until: string | null;

  @Type(() => Partner)
  subsidiaries: Partner[] | null;

  @Type(() => PartnerTag)
  tags: PartnerTag[];

  tax_number: string | null;

  total_credit_balance: number;

  total_unapplied_payments: number;

  timezone: string;

  total_overdue: number;

  get apPortalProviders(): ApPortalProvider[] {
    return (
      this.ap_portals?.reduce<ApPortalProvider[]>((acc, curr) => {
        const provider = curr.provider;

        if (!acc.includes(provider as ApPortalProvider)) {
          acc.push(provider as ApPortalProvider);
        }

        return acc;
      }, []) ?? []
    );
  }

  get abbreviatedName(): string {
    const name = this.computedName;
    if (!name) return '';
    return name
      .split(' ')
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase())
      .join('');
  }

  get computedName(): string {
    return this.name || this.legal_name || this.account_number || this.email || this.id;
  }

  get isOverdue(): boolean {
    if (!this.oldest_open_invoice_due_date) return false;
    // Normalize both dates to start of day for date-only comparison
    const dueDate = dayjs.utc(this.oldest_open_invoice_due_date).tz(defaultTimezone).startOf('day');
    const now = dayjs.utc().tz(defaultTimezone).startOf('day');
    if (!dueDate.isValid() || !now.isValid()) return false;
    // Partner becomes overdue the day AFTER the oldest invoice due date
    // e.g., due date 1/16: overdue starting 1/17
    return dueDate.isBefore(now, 'day');
  }

  get isGrouped(): boolean {
    return this.isGroupParent || this.isGroupChild;
  }

  get isGroupParent(): boolean {
    return (this.subsidiaries || []).length > 0;
  }

  get isGroupChild(): boolean {
    return Boolean(this.parent_id);
  }

  get groupId(): string | undefined {
    if (this.isGrouped) return this.parent_id || this.id;

    return undefined;
  }

  get subsidiaryCount(): number {
    return this.subsidiaries?.length || 0;
  }

  get daysOverdue(): number {
    if (!this.isOverdue) return 0;

    const dueDate = dayjs.utc(this.oldest_open_invoice_due_date).tz(defaultTimezone).startOf('day');
    if (!dueDate.isValid()) {
      console.error('Invalid date format for oldest_open_invoice_due_date');
      return 0;
    }
    const currentDay = dayjs().tz(defaultTimezone).startOf('day');
    return currentDay.diff(dueDate, 'days');
  }

  get allContacts(): Contact[] {
    const partnerAccountContact = new Contact({
      id: '-1',
      is_primary_email: this.is_primary_email,
      is_primary_phone: this.is_primary_phone,
      _name: this.name,
      given_name: this.given_name,
      family_name: this.family_name,
      is_email_disabled: this.is_email_disabled,
      is_phone_disabled: this.is_phone_disabled,
      email: this.email || '',
      phone: this.phone,
      is_sms_disabled: this.is_sms_disabled,
    });

    return [partnerAccountContact, ...(this.contacts || [])];
  }

  get partnerAccountContact(): Contact | undefined {
    return this.allContacts.find((contact) => contact.isPartnerAccountContact);
  }

  get primaryEmailContact(): Contact | undefined {
    return this.allContacts.find((contact) =>
      Boolean(!contact.internal_role && contact.is_primary_email && contact.email?.length),
    );
  }

  get primaryEmail() {
    return this.primaryEmailContact?.email;
  }

  get primaryPhoneContact(): Contact | undefined {
    return this.allContacts.find((contact) =>
      Boolean(!contact.internal_role && contact.is_primary_phone && contact.phone?.length),
    );
  }

  get primaryPhone() {
    return this.primaryPhoneContact?.phone;
  }

  get activeEmailContacts(): Contact[] | [] {
    const emailContacts = [
      ...(this.allContacts.filter(
        (contact) =>
          contact.email &&
          !contact.is_email_disabled &&
          !contact.is_primary_email &&
          !contact.internal_role,
      ) || []),
    ];

    if (this.primaryEmailContact) emailContacts.unshift(this.primaryEmailContact);

    return emailContacts;
  }

  get activePhoneContacts(): Contact[] | [] {
    const phoneContacts = [
      ...(this.allContacts.filter(
        (contact) =>
          contact.phone &&
          !contact.is_phone_disabled &&
          !contact.is_primary_phone &&
          !contact.internal_role,
      ) || []),
    ];

    if (this.primaryPhoneContact) phoneContacts.unshift(this.primaryPhoneContact);

    return phoneContacts;
  }

  get internalContacts(): Contact[] {
    return this.allContacts?.filter((c) => c.internal_role);
  }

  get rawTags(): Tag[] {
    return this.tags.map((partnerTag) => partnerTag.tag);
  }

  get totalBalance(): number {
    if (this.isGroupChild) return this.invoice_balance || 0;

    const subsidiaryBalance =
      this.subsidiaries?.reduce((acc, sub) => {
        return acc + (sub.invoice_balance || 0);
      }, 0) || 0;

    return this.invoice_balance + subsidiaryBalance;
  }

  get totalOverdueBalance(): number {
    if (this.isGroupChild) return this.total_overdue || 0;

    const subsidiaryOverdue =
      this.subsidiaries?.reduce((acc, sub) => {
        return acc + (sub.total_overdue || 0);
      }, 0) || 0;

    return this.total_overdue + subsidiaryOverdue;
  }

  get totalOpenInvoices(): number {
    if (this.isGroupChild) return this.num_open_invoices || 0;

    const subsidiaryInvoices =
      this.subsidiaries?.reduce((acc, sub) => {
        return acc + (sub.num_open_invoices || 0);
      }, 0) || 0;

    return this.num_open_invoices + subsidiaryInvoices;
  }

  get isOutreachPaused(): boolean {
    return Boolean(
      this.is_email_outreach_manually_paused ||
      this.is_call_outreach_manually_paused ||
      this.dunning_state?.action === DunningStateAction.OUTREACH_PAUSE,
    );
  }
}

export class PartnerTag extends Model {
  partner_id: string;
  tag_id: string;
  is_manual: boolean;

  @Type(() => Tag)
  tag: Tag;
}
